import React from 'react';
import { useTranslation } from 'react-i18next';
import { format, parse } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Calendar, CheckCircle2, Clock } from 'lucide-react';

interface MonthlyTotal {
  month: string; // "2026-01"
  hours: number;
  days: number;
  overtime: number;
  onCall: number;
  approvedCount: number;
  pendingCount: number;
}

interface MonthlyTotalsTableProps {
  data: MonthlyTotal[];
  className?: string;
}

export const MonthlyTotalsTable: React.FC<MonthlyTotalsTableProps> = ({
  data,
  className,
}) => {
  const { t } = useTranslation();

  const formatMonth = (monthStr: string) => {
    try {
      const date = parse(monthStr, 'yyyy-MM', new Date());
      return format(date, 'MMM yyyy');
    } catch {
      return monthStr;
    }
  };

  const getStatusBadge = (approved: number, pending: number) => {
    if (pending > 0) {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          <Clock className="h-3 w-3 mr-1" />
          {pending} {t('dashboard.pending', 'pending')}
        </Badge>
      );
    }
    if (approved > 0) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t('dashboard.allApproved', 'All approved')}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-muted-foreground">
        {t('dashboard.noEntries', 'No entries')}
      </Badge>
    );
  };

  if (data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4 text-orange-500" />
            {t('dashboard.monthlyTotals', 'Monthly Totals')}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[150px] flex items-center justify-center text-muted-foreground">
          {t('dashboard.noMonthlyData', 'No monthly data available')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-4 w-4 text-orange-500" />
          {t('dashboard.monthlyTotals', 'Monthly Totals')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Phone: one block per month, no horizontal scroll */}
        <div className="max-h-[320px] space-y-2 overflow-y-auto md:hidden">
          {data.map((row) => (
            <div key={row.month} className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{formatMonth(row.month)}</span>
                {getStatusBadge(row.approvedCount, row.pendingCount)}
              </div>
              <dl className="mt-2 grid grid-cols-4 gap-1 text-center text-[11px] leading-tight">
                <div>
                  <dt className="text-muted-foreground">{t('dashboard.hours', 'Hours')}</dt>
                  <dd className="font-semibold">{row.hours.toFixed(1)}h</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('dashboard.days', 'Days')}</dt>
                  <dd className="font-semibold">{row.days}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('dashboard.overtime', 'Overtime')}</dt>
                  <dd className="font-semibold">{row.overtime.toFixed(1)}h</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('dashboard.onCall', 'On-Call')}</dt>
                  <dd className="font-semibold">{row.onCall.toFixed(1)}h</dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
        <div className="hidden max-h-[300px] overflow-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('dashboard.month', 'Month')}</TableHead>
                <TableHead className="text-right">{t('dashboard.hours', 'Hours')}</TableHead>
                <TableHead className="text-right">{t('dashboard.days', 'Days')}</TableHead>
                <TableHead className="text-right">{t('dashboard.overtime', 'Overtime')}</TableHead>
                <TableHead className="text-right">{t('dashboard.onCall', 'On-Call')}</TableHead>
                <TableHead>{t('dashboard.status', 'Status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.month}>
                  <TableCell className="font-medium">{formatMonth(row.month)}</TableCell>
                  <TableCell className="text-right">{row.hours.toFixed(1)}h</TableCell>
                  <TableCell className="text-right">{row.days}</TableCell>
                  <TableCell className="text-right">{row.overtime.toFixed(1)}h</TableCell>
                  <TableCell className="text-right">{row.onCall.toFixed(1)}h</TableCell>
                  <TableCell>{getStatusBadge(row.approvedCount, row.pendingCount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyTotalsTable;
